"use client"

import Image from "next/image"
import { cn } from "@/utils/utils"
import { Card, CardContent } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useLoginMutation } from "@/hooks/useAuthQuery"
import { LogInIcon, ShieldCheckIcon, ZapIcon } from "lucide-react"
import { LoadingButton } from "@/components/common/loading-button"
import { ServerStatus } from "@/features/auth/components/server-status"

const loginSchema = z.object({
  email: z.string().email("E-mail inválido"),
  password: z.string().min(6, "A senha deve ter pelo menos 6 caracteres"),
})

type LoginFormData = z.infer<typeof loginSchema>

// Login automático apenas em desenvolvimento. As credenciais podem ser
// sobrescritas por variáveis de ambiente; o padrão corresponde ao seed
// da plataforma (PlatformSeeder: admin@saas.local / password).
const isDev = process.env.NODE_ENV === "development"
const devCredentials = {
  email: process.env.NEXT_PUBLIC_DEV_LOGIN_EMAIL || "admin@saas.local",
  password: process.env.NEXT_PUBLIC_DEV_LOGIN_PASSWORD || "password",
}

export function LoginForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { mutate: loginMutation, isPending, error } = useLoginMutation()

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  function onSubmit(data: LoginFormData) {
    loginMutation(data)
  }

  function handleDevLogin() {
    form.setValue("email", devCredentials.email)
    form.setValue("password", devCredentials.password)
    loginMutation(devCredentials)
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <form className="p-6 md:p-8" onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <ShieldCheckIcon className="size-6" />
                </div>
                <h1 className="text-2xl font-bold">Super Painel</h1>
                <p className="text-balance text-muted-foreground">
                  Acesso restrito à gestão da plataforma
                </p>
              </div>
              <Field>
                <FieldLabel htmlFor="email">E-mail</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@exemplo.com"
                  {...form.register("email")}
                />
                {form.formState.errors.email && (
                  <span className="text-sm text-destructive">
                    {form.formState.errors.email.message}
                  </span>
                )}
              </Field>
              <Field>
                <FieldLabel htmlFor="password">Senha</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  {...form.register("password")}
                />
                {form.formState.errors.password && (
                  <span className="text-sm text-destructive">
                    {form.formState.errors.password.message}
                  </span>
                )}
                {!!error && (
                  <span className="text-sm text-destructive">
                    {(error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Credenciais inválidas'}
                  </span>
                )}
              </Field>
              <Field>
                <LoadingButton type="submit" loading={isPending}>
                  <LogInIcon data-icon="inline-start" /> Entrar
                </LoadingButton>
                {isDev && (
                  <LoadingButton
                    type="button"
                    variant="outline"
                    loading={isPending}
                    onClick={handleDevLogin}
                  >
                    <ZapIcon data-icon="inline-start" /> Login automático (dev)
                  </LoadingButton>
                )}
              </Field>
            </FieldGroup>
          </form>
          <div className="relative hidden bg-muted md:block">
            <Image
              src="/login-hero.jpg"
              alt="Login hero image"
              fill
              className="object-cover dark:brightness-[0.6]"
              sizes="(min-width: 768px) 50vw, 100vw"
            />
          </div>
        </CardContent>
      </Card>
      <ServerStatus />
      <FieldDescription className="px-6 text-center">
        Painel de gestão do SaaS — uso exclusivo da administração.
      </FieldDescription>
    </div>
  )
}
